import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SpinnerComponent } from '../../shared/components/spinner.component';
import { BannerService, Banner } from '../../core/banner.service';
import { NotificationService } from '../../core/notification.service';
import { BlobService } from '../../core/blob.service';
import { HttpEventType } from '@angular/common/http';

@Component({
  standalone: true,
  selector: 'app-banners-manager',
  imports: [CommonModule, FormsModule, SpinnerComponent],
  template: `
    <div class="bg-white p-6 rounded shadow">
      <div class="flex items-center justify-between mb-6">
        <h1 class="text-3xl font-serif">Gestão de Banners</h1>
        <button (click)="openForm()" class="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90">
          + Novo Banner
        </button>
      </div>

      <!-- Lista de Banners -->
      <div class="space-y-4">
        <div *ngFor="let banner of banners" class="border rounded-lg p-4 hover:shadow-md transition">
          <div class="flex gap-4">
            <!-- Preview da Imagem -->
            <div class="w-48 h-32 bg-surface rounded overflow-hidden flex-shrink-0">
              <img [src]="banner.imagemUrl" [alt]="banner.titulo" class="w-full h-full object-cover">
            </div>

            <!-- Informações -->
            <div class="flex-1">
              <div class="flex items-start justify-between mb-2">
                <div>
                  <h3 class="font-semibold text-lg">{{ banner.titulo }}</h3>
                  <p *ngIf="banner.descricao" class="text-sm text-muted">{{ banner.descricao }}</p>
                </div>
                <div class="flex items-center gap-2">
                  <span [class]="banner.ativo ? 'px-2 py-1 bg-green-100 text-green-800 rounded text-xs' : 'px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs'">
                    {{ banner.ativo ? 'Ativo' : 'Inativo' }}
                  </span>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-2 text-sm mb-3">
                <div>
                  <span class="text-muted">Posição:</span>
                  <span class="ml-1 font-medium">{{ getPosicaoLabel(banner.posicao) }}</span>
                </div>
                <div>
                  <span class="text-muted">Ordem:</span>
                  <span class="ml-1 font-medium">{{ banner.ordem }}</span>
                </div>
                <div *ngIf="banner.linkDestino">
                  <span class="text-muted">Link:</span>
                  <a [href]="banner.linkDestino" target="_blank" class="ml-1 text-primary hover:underline text-xs">
                    {{ banner.linkDestino }}
                  </a>
                </div>
                <div *ngIf="banner.dataInicio || banner.dataFim">
                  <span class="text-muted">Vigência:</span>
                  <span class="ml-1 text-xs">
                    {{ banner.dataInicio | date:'dd/MM/yyyy' }} - {{ banner.dataFim | date:'dd/MM/yyyy' }}
                  </span>
                </div>
              </div>

              <!-- Ações -->
              <div class="flex gap-2">
                <button (click)="toggleActive(banner)" class="text-xs px-3 py-1 border rounded hover:bg-surface">
                  {{ banner.ativo ? 'Desativar' : 'Ativar' }}
                </button>
                <button (click)="openForm(banner)" class="text-xs px-3 py-1 border rounded hover:bg-surface">
                  Editar
                </button>
                <button (click)="deleteBanner(banner)" class="text-xs px-3 py-1 border border-red-500 text-red-600 rounded hover:bg-red-50">
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>

        <div *ngIf="banners.length === 0 && !loading" class="text-center py-12 text-muted">
          <div class="text-6xl mb-4">🖼️</div>
          <p class="text-lg">Nenhum banner cadastrado</p>
          <button (click)="openForm()" class="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90">
            Criar Primeiro Banner
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div *ngIf="loading" class="text-center py-8">
        <app-spinner></app-spinner>
      </div>
    </div>

    <!-- Modal de Formulário -->
    <div *ngIf="showForm" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto" (click)="closeForm()">
      <div class="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 my-8" (click)="$event.stopPropagation()">
        <h2 class="text-2xl font-semibold mb-4">{{ editingBanner ? 'Editar Banner' : 'Novo Banner' }}</h2>
        
        <form (ngSubmit)="saveBanner()" class="space-y-4">
          <div>
            <label class="block text-sm font-medium mb-1">Título *</label>
            <input type="text" [(ngModel)]="formData.titulo" name="titulo" required class="w-full px-3 py-2 border rounded">
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">Descrição</label>
            <textarea [(ngModel)]="formData.descricao" name="descricao" rows="2" class="w-full px-3 py-2 border rounded"></textarea>
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">Imagem *</label>
            <input type="file" (change)="onFileSelected($event)" accept="image/*" class="w-full px-3 py-2 border rounded">
            <div *ngIf="uploadProgress > 0 && uploadProgress < 100" class="mt-2">
              <div class="h-2 bg-gray-200 rounded overflow-hidden">
                <div class="h-full bg-primary transition-all" [style.width.%]="uploadProgress"></div>
              </div>
            </div>
            <div *ngIf="formData.imagemUrl" class="mt-2">
              <img [src]="formData.imagemUrl" class="w-32 h-20 object-cover rounded border">
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium mb-1">Link de Destino</label>
            <input type="url" [(ngModel)]="formData.linkDestino" name="linkDestino" placeholder="https://" class="w-full px-3 py-2 border rounded">
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium mb-1">Posição *</label>
              <select [(ngModel)]="formData.posicao" name="posicao" required class="w-full px-3 py-2 border rounded">
                <option value="home-hero">Home - Hero</option>
                <option value="home-middle">Home - Middle</option>
                <option value="sidebar">Sidebar</option>
                <option value="footer">Footer</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium mb-1">Ordem *</label>
              <input type="number" [(ngModel)]="formData.ordem" name="ordem" min="0" required class="w-full px-3 py-2 border rounded">
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium mb-1">Data Início</label>
              <input type="date" [(ngModel)]="formData.dataInicio" name="dataInicio" class="w-full px-3 py-2 border rounded">
            </div>

            <div>
              <label class="block text-sm font-medium mb-1">Data Fim</label>
              <input type="date" [(ngModel)]="formData.dataFim" name="dataFim" class="w-full px-3 py-2 border rounded">
            </div>
          </div>

          <div class="flex items-center">
            <input type="checkbox" [(ngModel)]="formData.ativo" name="ativo" id="ativo" class="mr-2">
            <label for="ativo" class="text-sm font-medium">Banner ativo</label>
          </div>

          <div class="flex justify-end gap-2 pt-4 border-t">
            <button type="button" (click)="closeForm()" class="px-4 py-2 border rounded hover:bg-surface">
              Cancelar
            </button>
            <button type="submit" [disabled]="!formData.titulo || !formData.imagemUrl || uploading" class="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50">
              {{ uploading ? 'Salvando...' : 'Salvar Banner' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  `
})
export class BannersManagerComponent implements OnInit {
  banners: Banner[] = [];
  loading = false;
  showForm = false;
  editingBanner: Banner | null = null;
  uploading = false;
  uploadProgress = 0;

  formData: Partial<Banner> = {
    titulo: '',
    descricao: '',
    imagemUrl: '',
    linkDestino: '',
    posicao: 'home-hero',
    ordem: 0,
    ativo: true,
    dataInicio: '',
    dataFim: ''
  };

  constructor(
    private bannerSvc: BannerService,
    private blobSvc: BlobService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadBanners();
  }

  loadBanners() {
    this.loading = true;
    this.bannerSvc.list().subscribe({
      next: (banners) => {
        this.banners = banners.sort((a, b) => a.ordem - b.ordem);
        this.loading = false;
      },
      error: (err) => {
        console.error('[Banners] Erro ao carregar banners', err);
        this.notification.error('Erro ao carregar banners');
        this.loading = false;
      }
    });
  }

  openForm(banner?: Banner) {
    this.editingBanner = banner || null;
    if (banner) {
      this.formData = { ...banner };
    } else {
      this.formData = {
        titulo: '',
        descricao: '',
        imagemUrl: '',
        linkDestino: '',
        posicao: 'home-hero',
        ordem: this.banners.length,
        ativo: true
      };
    }
    this.showForm = true;
  }

  closeForm() {
    this.showForm = false;
    this.editingBanner = null;
    this.uploadProgress = 0;
  }

  onFileSelected(event: any) {
    const file = event.target.files?.[0];
    if (!file) return;

    const fd = new FormData();
    fd.append('file', file);
    fd.append('filename', file.name);
    fd.append('contentType', file.type);

    this.uploading = true;
    this.uploadProgress = 0;

    this.blobSvc.uploadMedia(fd).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.uploadProgress = Math.round((100 * event.loaded) / (event.total || event.loaded));
        } else if (event.type === HttpEventType.Response) {
          this.formData.imagemUrl = event.body?.url || '';
          this.uploading = false;
          this.uploadProgress = 100;
        }
      },
      error: (err) => {
        console.error('[Banners] Erro ao fazer upload', err);
        this.notification.error('Erro ao fazer upload da imagem');
        this.uploading = false;
        this.uploadProgress = 0;
      }
    });
  }

  saveBanner() {
    if (!this.formData.titulo || !this.formData.imagemUrl) {
      this.notification.error('Preencha todos os campos obrigatórios');
      return;
    }

    this.uploading = true;

    const operation = this.editingBanner
      ? this.bannerSvc.update(this.editingBanner.id, this.formData)
      : this.bannerSvc.create(this.formData);

    operation.subscribe({
      next: () => {
        this.notification.success(this.editingBanner ? 'Banner atualizado' : 'Banner criado');
        this.closeForm();
        this.loadBanners();
      },
      error: (err) => {
        console.error('[Banners] Erro ao salvar banner', err);
        this.notification.error('Erro ao salvar banner');
        this.uploading = false;
      }
    });
  }

  toggleActive(banner: Banner) {
    this.bannerSvc.toggleActive(banner.id).subscribe({
      next: () => {
        this.notification.success(`Banner ${banner.ativo ? 'desativado' : 'ativado'}`);
        this.loadBanners();
      },
      error: (err) => {
        console.error('[Banners] Erro ao alterar status', err);
        this.notification.error('Erro ao alterar status do banner');
      }
    });
  }

  deleteBanner(banner: Banner) {
    if (!confirm(`Excluir banner "${banner.titulo}"?`)) return;

    this.loading = true;
    this.bannerSvc.delete(banner.id).subscribe({
      next: () => {
        this.notification.success('Banner excluído');
        this.loadBanners();
      },
      error: (err) => {
        console.error('[Banners] Erro ao excluir banner', err);
        this.notification.error('Erro ao excluir banner');
        this.loading = false;
      }
    });
  }

  getPosicaoLabel(posicao: string): string {
    const labels: any = {
      'home-hero': 'Home - Hero',
      'home-middle': 'Home - Middle',
      'sidebar': 'Sidebar',
      'footer': 'Footer'
    };
    return labels[posicao] || posicao;
  }
}
